
import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import Icon from './Icon';

interface HelpModalProps {
    isOpen: boolean;
    onClose: () => void;
    activeTool: string | null;
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose, activeTool }) => {
    const { t } = useLanguage();
    if (!isOpen) return null;

    const getToolHelp = () => {
        switch(activeTool) {
            case 'match_maker': return { 
                title: t('toolMatchMaker'), 
                desc: t('toolMatchMakerDesc'), 
                steps: [t('aboutStep1Desc'), t('aboutStep2Desc'), t('aboutStep3Desc')] 
            };
            case 'nameplate': return { 
                title: t('toolNameplate'), 
                desc: t('toolNameplateDesc'), 
                steps: ["Enter the name text.", "Choose single or dual style.", "Select font and material.", "Generate 3D render."] 
            };
            case 'sketch_to_real': return { 
                title: t('toolSketch'), 
                desc: t('toolSketchDesc'), 
                steps: ["Upload your rough sketch.", "Select metal and gemstones.", "AI converts it to photorealism."] 
            };
            case 'mockup_generator': return { 
                title: t('toolMockup'), 
                desc: t('toolMockupDesc'), 
                steps: ["Upload a product image.", "Choose a model or studio scene.", "Generate a professional photoshoot."] 
            };
            default: return { title: "Studio Tools", desc: "Select a tool to begin.", steps: [] };
        }
    };

    const content = getToolHelp();

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
            <div className="w-full max-w-md bg-white dark:bg-[#1d1d1f] border border-black/5 dark:border-white/10 rounded-3xl shadow-2xl overflow-hidden">
                <div className="p-6 border-b border-black/5 dark:border-white/5 flex justify-between items-center bg-slate-50/50 dark:bg-white/5">
                    <h2 className="text-lg font-display font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <Icon name="help" className="w-5 h-5 text-apple-gold" />
                        {content.title}
                    </h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
                        <Icon name="close" className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6 space-y-6">
                    <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{content.desc}</p>
                    <div className="space-y-4">
                        <h3 className="text-xs font-bold uppercase tracking-widest text-slate-900 dark:text-white">How it works</h3>
                        <ul className="space-y-4">
                            {content.steps.map((step, i) => (
                                <li key={i} className="flex gap-4 text-sm text-slate-600 dark:text-slate-300">
                                    <span className="w-6 h-6 rounded-full bg-apple-gold/10 text-apple-gold flex items-center justify-center text-[10px] font-bold shrink-0">{i+1}</span>
                                    <span className="flex-1 pt-0.5">{step}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className="p-4 bg-slate-50/50 dark:bg-white/5">
                    <button onClick={onClose} className="w-full py-3 bg-slate-900 dark:bg-white text-white dark:text-black rounded-xl font-bold text-sm">Got it</button>
                </div>
            </div>
        </div>
    );
};

export default HelpModal;
